import React, { useEffect, useState, Fragment } from 'react';
import Modal from 'react-modal';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box,
    Typography,
    Button,
    Container,
    Paper,
    Divider
} from '@material-ui/core';
import { snedMail } from '../../actions/mailActions';

import history from '../../history';
import MailForm from './MailForm';
import MailPreview from './MailPreview';
import useModal from '../hooks/useModal';
import Alert from '../../utils/alert';
import Spinner from '../common/Spinner';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
    },
    container: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: theme.spacing(2),
    },
    heading: {
        textAlign: 'center',
        color: theme.palette.primary.main,
        marginBottom: '4rem',
        fontSize: '2.4rem',
        fontWeight: 'bold',
    },
    subHeading: {
        fontSize: '1.8rem',
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
    infoText: {
        color: theme.palette.info.main,
        fontSize: '1.4rem',
        marginBottom: theme.spacing(1.5),
    },
    recordInfoContainer: {
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(2),
        marginBottom: theme.spacing(3),
        borderRadius: theme.shape.borderRadius,
    },
    recordTitle: {
        fontSize: '1.6rem',
        fontWeight: 600,
        marginBottom: theme.spacing(1),
        color: theme.palette.text.primary,
    },
    recordDetails: {
        whiteSpace: 'pre-wrap',
        maxHeight: '30rem',
        overflowY: 'auto',
        fontSize: '1.4rem',
        lineHeight: 1.6,
        color: theme.palette.text.secondary,
    },
    recordAuthor: {
        fontSize: '1.4rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1),
    },
    modalContent: {
        maxWidth: '60rem',
        maxHeight: '90vh',
        padding: '2rem',
        margin: 'auto',
        backgroundColor: theme.palette.common.white,
        borderRadius: theme.shape.borderRadius,
        outline: 'none',
        overflow: 'auto',
    },
    modalOverlay: {
        zIndex: 1000,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    modalTitle: {
        fontSize: '1.6rem',
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
    buttonContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    sendButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    cancelButton: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
    },
}));

const MailHome = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [formData, setFormData] = useState(null);
    const [openModal, closeModal, modalIsOpen] = useModal();

    const recordInfo = useSelector(state => state.mail.recordInfo);
    const sending = useSelector(state => state.mail.sending);
    const errors = useSelector(state => state.mail.errors);
    const user = useSelector(state => state.user.currentUser);

    useEffect(() => {
        if (errors) {
            console.log(errors);
            history.push('/');
            Alert.error(errors);
        }
    }, [errors])

    const onSubmit = (data) => {
        setFormData(data);
        openModal();
    }

    const sendEmail = () => {
        //dispatch
        dispatch(snedMail(recordInfo.type, recordInfo.id, formData, () => {
            if (recordInfo.type === 'user') {
                history.push('personalprofiles')
            }
            else {
                history.push(`${recordInfo.type}/${recordInfo.id}`)
            }
        }));
    }

    const renderRecordInfo = () => {
        switch (recordInfo.type) {
            case "thread":
                return renderThreadRecordInfo();
            case "user":
                return renderUserRecordInfo();
            default:
                return null;
        }
    }

    const renderUserRecordInfo = () => {
        return (
            <Fragment>
                <Typography className={classes.infoText}>
                    以下のユーザーにメールを送信します
                </Typography>
                <Paper className={classes.recordInfoContainer} elevation={1}>
                    <Typography className={classes.recordTitle}>
                        {recordInfo.userName}
                    </Typography>
                    <Typography className={classes.recordDetails}>
                        {recordInfo.details}
                    </Typography>
                </Paper>
            </Fragment>
        )
    }

    const renderThreadRecordInfo = () => {
        return (
            <Fragment>
                <Typography className={classes.infoText}>
                    以下の投稿について問い合わせをします
                </Typography>
                <Paper className={classes.recordInfoContainer} elevation={1}>
                    <Typography className={classes.recordTitle}>
                        {recordInfo.title}
                    </Typography>
                    <Typography className={classes.recordDetails}>
                        {recordInfo.details}
                    </Typography>
                    <Divider style={{ margin: '1rem 0' }} />
                    <Typography className={classes.recordAuthor}>
                        投稿者: {recordInfo.userName}
                    </Typography>
                </Paper>
            </Fragment>
        )
    }

    return (
        <div className={classes.root}>
            <Container className={classes.container}>
                <Typography variant="h1" className={classes.heading}>
                    メールを送る
                </Typography>
                {renderRecordInfo()}
                <Typography variant="h2" className={classes.subHeading}>
                    メールフォーム
                </Typography>
                <MailForm onSubmit={onSubmit} name={user ? user.name : ''} email={user ? user.email : ''} title={`${recordInfo.title}`} />
                <Modal
                    isOpen={modalIsOpen}
                    onRequestClose={closeModal}
                    className={classes.modalContent}
                    style={{
                        overlay: {
                            zIndex: 1000,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        },
                        content: {
                            maxWidth: '60rem',
                            maxHeight: '90vh',
                            padding: '2rem',
                            margin: 'auto',
                            border: 'none',
                            borderRadius: '8px',
                            outline: 'none',
                            overflow: 'auto',
                        }
                    }}
                >
                    <Typography className={classes.modalTitle}>
                        以下の内容でメールを送信します。よろしいですか？
                    </Typography>
                    <Divider style={{ margin: '1rem 0' }} />
                    <MailPreview formData={formData} />
                    <Divider style={{ margin: '1rem 0' }} />
                    {sending ? (
                        <Spinner />
                    ) : (
                        <Box className={classes.buttonContainer}>
                            <Button
                                type="submit"
                                onClick={sendEmail}
                                className={classes.sendButton}
                                variant="contained"
                            >
                                送信
                            </Button>
                            <Button
                                type="button"
                                className={classes.cancelButton}
                                onClick={closeModal}
                                variant="contained"
                            >
                                キャンセル
                            </Button>
                        </Box>
                    )}
                </Modal>
            </Container>
        </div>
    )
}

export default MailHome
